import React from 'react';
import styled from 'App/styled';
import { useDispatch, useSelector } from 'react-redux';
import { pushEvent } from '@nykaa/nes-data-layer';
import { SearchSuggestion } from 'types/searchSuggestion';
import {
  getHighlightedSearchField,
  addEllipses,
} from 'helpers/utils/search/searchSuggestion';
import TapOn from 'components/Icons/TapOn';
import {
  fetchSearchSuggestion,
  updateSearchField,
} from 'store/searchSuggestions/action';
import { State } from 'store/types';
import { isEligible } from 'store/app/selector';
import ArrowRight from '@nykaa/nes-ui-components/Icons/arrow-up-right';
import ChevronRight from '@nykaa/nes-ui-components/Icons/chevron-right';
import { getEligibleVariantName } from 'helpers/experimentV2';
import { AB_SEARCHCOUNT_MWEB } from 'constants/experiments';
import { AUTO_SUGGESTION_ARROW_CLICKED } from 'constants/dataLayer';

const SuggestionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0;
  padding: 12px 16px;
`;

const Button = styled.button`
  width: 100%;
  text-decoration: none;
  color: ${(props) => props.theme.darkGrey};
  border: none;
  background: #fff;
  ${({ theme }) => theme.typography.bodyMedium};
  text-align: start;
  display: flex;
  justify-content: center;
  align-items: center;
  &:active {
    background: #e6e9eb;
  }
`;

const Icon = styled.span`
  height: 24px;
  margin-inline-end: 16px;
  opacity: 0.48;
  img {
    width: 24px;
  }
`;
const Text = styled.span`
  width: 100%;
  color: #001325;
  overflow: hidden;
  &.multiline-elpisses {
    overflow: hidden;
    position: relative;
    line-height: 20px;
    max-height: 40px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  }
`;

const Title = styled.span`
  margin-inline-end: 5px;
  ${({ theme }) => theme.typography.bodyMedium};
  width: 100%;

  b {
    ${({ theme }) => theme.typography.subTitleMedium};
  }
`;

const Subtext = styled.span`
  ${({ theme }) => theme.typography.bodySmall};
  color: #89929a;
  display: inline-block;
`;

const ImageBox = styled.span`
  margin-left: auto;
  img {
    height: 48px;
    width: 48px;
    border: 1px solid #d8d8d8;
  }
`;

const TapOnWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 20px;
`;

const CountWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.spacing60};
`;

const AutoSuggestionCount = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  ${({ theme }) => theme.typography.bodySmall};
`;

const StyledChevronRight = styled.span`
  display: flex;
  align-items: center;

  svg {
    display: flex;
    align-items: center;
    width: ${({ theme }) => theme.spacing.spacing100};
    height: ${({ theme }) => theme.spacing.spacing100};
    path {
      fill: ${({ theme }) => theme.colors.textSecondary};
    }
  }
`;

const StyledArrowUpRight = styled.span`
  display: flex;
  align-items: center;

  svg {
    display: flex;
    align-items: center;
    width: ${({ theme }) => theme.spacing.spacing80};
    height: ${({ theme }) => theme.spacing.spacing80};
    path {
      fill: ${({ theme }) => theme.colors.textSecondary};
    }
    transform: rotate(-90deg);
  }
`;

interface SearchSuggestionCardProps {
  suggestion: SearchSuggestion;
  searchField: string;
  index: number;
  handleClick: (suggestion: SearchSuggestion, position: number) => void;
  searchSuggestionIcons: any;
}

function SearchSuggestionCard({
  suggestion,
  searchField,
  handleClick,
  index,
  searchSuggestionIcons,
}: SearchSuggestionCardProps) {
  const runValue = useSelector((state: State) => state.appReducer.run);
  const isPro = useSelector((state: State) => state.auth.user.isPro);
  const tapOnConfigs = useSelector(
    (state: State) => state.remoteConfig.configs.AB.autocompleteTapOn
  );
  const searchVariant = useSelector((state: State) =>
    getEligibleVariantName(state, AB_SEARCHCOUNT_MWEB)
  );
  const isSearchVariantA = searchVariant === 'A';
  const isSearchVariantB = searchVariant === 'B';
  const isTapOnEnabled = isEligible(tapOnConfigs, runValue);
  const { q, image, subtext, icon, matchCount = '' } = suggestion || {};

  const geticonUrl = (param: string) => {
    if (param in searchSuggestionIcons) {
      return searchSuggestionIcons[param];
    } else {
      return searchSuggestionIcons.query;
    }
  };

  const totalCharCount = image ? 75 : 85;
  const subTextLength = subtext && subtext.length > 0 ? subtext.length : 0;
  const actualMaxChar = totalCharCount - subTextLength;
  const actualMinChar = actualMaxChar - 3;
  let sortedQuery;
  if (subTextLength == 0) {
    sortedQuery = q;
  } else {
    sortedQuery = addEllipses(q, actualMaxChar, actualMinChar);
  }

  const { partBefore, partMatching, partAfter, match } =
    getHighlightedSearchField(searchField, sortedQuery);
  const queryText = match ? (
    <Title>
      {partBefore}
      <b>{partMatching}</b>
      {partAfter}
    </Title>
  ) : (
    q
  );

  const dispatch = useDispatch();
  const handleTapOn = () => {
    const updatedQuery = `${q} `;
    updateSearchField(updatedQuery)(dispatch);
  };
  const handleIconClick = (e: MouseEvent) => {
    e.stopPropagation();
    updateSearchField(q)(dispatch);
    pushEvent(AUTO_SUGGESTION_ARROW_CLICKED, {
      searchKeyword: q,
      searchTypedKeyword: searchField,
    });
    fetchSearchSuggestion(q, isPro)(dispatch);
  };
  return (
    <SuggestionWrapper>
      <Button
        key={q}
        type="button"
        onClick={() => handleClick(suggestion, index)}
      >
        <Icon>
          <img
            src={geticonUrl(icon)}
            alt="icon"
          />
        </Icon>

        <Text className={subTextLength == 0 ? 'multiline-elpisses' : ''}>
          {queryText}
          {subtext && <Subtext> {subtext} </Subtext>}
        </Text>
        {matchCount && (isSearchVariantA || isSearchVariantB) && (
          <CountWrapper>
            <AutoSuggestionCount>{matchCount}</AutoSuggestionCount>
            {isSearchVariantB && (
              <StyledArrowUpRight>
                <ArrowRight onClick={handleIconClick} />
              </StyledArrowUpRight>
            )}
            {isSearchVariantA && (
              <StyledChevronRight>
                <ChevronRight />
              </StyledChevronRight>
            )}
          </CountWrapper>
        )}

        {image && (
          <ImageBox>
            <img
              src={image}
              alt="product"
            />
          </ImageBox>
        )}
      </Button>

      {!image && isTapOnEnabled && (
        <TapOnWrapper onClick={() => handleTapOn()}>
          <TapOn />
        </TapOnWrapper>
      )}
    </SuggestionWrapper>
  );
}

export default SearchSuggestionCard;
